import 'dayjs/locale/en-gb';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import 'dayjs/locale/th';
import 'dayjs/locale/vi';
import 'dayjs/locale/ms-my';
import 'dayjs/locale/id';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/ar';
import 'dayjs/locale/es';
import 'dayjs/locale/pt-br';

import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import localeData from 'dayjs/plugin/localeData';
import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekday from 'dayjs/plugin/weekday';

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(localeData);
dayjs.extend(minMax);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.extend(duration);

dayjs.Ls.en.weekStart = 1;

export const DATETIME_SECONDS_FORMAT = 'YYYY-MM-DD HH:mm:ss';
let dayjsTZ = dayjs.tz.guess() ?? 'Asia/Singapore';

export default dayjs;

export type Dayjs = dayjs.Dayjs;

export type TDayJsInput = string | number | Date | Dayjs | null;

export interface IGetUTCOffsetDateTimeAsDayjsParams {
  input?: Date | number;
  utcOffset?: number;
  keepTime?: boolean;
  baseFromOffset?: boolean;
}

export interface IGetStartOrEndOfPeriodParams {
  date?: TDayJsInput;
  isStartOf?: boolean;
  unit?: EDayjsUnit;
  tz?: string;
}

export enum EDayjsUnit {
  Hour = 'hour',
  Day = 'day',
  Week = 'week',
  Month = 'month',
  Year = 'year'
}

export const setUserTimeZone = (tz: string) => {
  dayjsTZ = tz;
};

export const getUserTimeZone = () => dayjsTZ;

/**
 * To CONVERT input into timezone aware dayjs object.
 *
 * @param date: Input to convert
 * @param tz: Timezone to convert input to
 */
export const toDayjs = (date?: TDayJsInput | null, tz: string = dayjsTZ) =>
  dayjs(date === null ? undefined : date).tz(tz);

/**
 * To CONVERT input into timezone aware dayjs object (without changing the time with reference to the timezone, a.k.a. just adding the Timezone value to the object without correcting the time to the timezone)
 *
 * @param date: Input to convert
 * @param tz: Timezone to convert input to
 */
export const toDayjsKeepTime = (date?: TDayJsInput, tz = dayjsTZ) =>
  dayjs(date === null ? undefined : date).tz(tz, true);

/**
 * To PARSE input into timezone aware dayjs object, in the specified timezone
 *
 * @param date: Input to convert
 * @param tz: Timezone to convert input to
 */
export const parseDayjs = (date?: TDayJsInput, tz = dayjsTZ) => dayjs.tz(date === null ? undefined : date, tz);

export const setDayjsTZ = (date: Dayjs, tz = dayjsTZ) => date.tz(tz);

export const setDayjsTZKeepTime = (date: Dayjs, tz = dayjsTZ) => date.tz(tz, true);

export const formatDayjs = (date?: TDayJsInput, formatStr = DATETIME_SECONDS_FORMAT, tz = dayjsTZ) =>
  toDayjs(date, tz).format(formatStr);

export const formatDayjsKeepTime = (date?: TDayJsInput, formatStr = DATETIME_SECONDS_FORMAT, tz = dayjsTZ) =>
  toDayjsKeepTime(date, tz).format(formatStr);

export const getSecondsSinceEpoch = (date?: Dayjs, excludeMs?: boolean) =>
  excludeMs ? date?.unix() : date?.millisecond(0).valueOf();

export const getStartOrEndOfPeriod = ({
  date,
  isStartOf = true,
  unit = EDayjsUnit.Day,
  tz = dayjsTZ
}: IGetStartOrEndOfPeriodParams) => (isStartOf ? toDayjs(date, tz).startOf(unit) : toDayjs(date, tz).endOf(unit));

export const getStartOrEndOfPeriodInSeconds = ({ date, ...restParams }: IGetStartOrEndOfPeriodParams) =>
  date ? getSecondsSinceEpoch(getStartOrEndOfPeriod({ date, ...restParams })) : undefined;

export const getCurrentMonthDateRange = (disableFutureDates?: boolean): [Date, Date] => {
  const today = dayjs();

  return [
    today.startOf('month').toDate(),
    disableFutureDates
      ? getMinimumDate(today.endOf('day'), today.endOf('month')).toDate()
      : today.endOf('month').toDate()
  ];
};

export const getLastMonthDateRange = (): [Date, Date] => {
  const lastMonth = dayjs().subtract(1, 'month');

  return [lastMonth.startOf('month').toDate(), lastMonth.endOf('month').toDate()];
};

export const getPast6MonthsDateRange = (): [Date, Date] => {
  const lastMonth = dayjs().subtract(1, 'month');
  const last6Month = dayjs().subtract(6, 'months');

  return [last6Month.startOf('month').toDate(), lastMonth.endOf('month').toDate()];
};

export const getCurrentYearDateRange = (disableFutureDates?: boolean): [Date, Date] => {
  const today = dayjs();

  return [
    today.startOf('year').toDate(),
    disableFutureDates ? getMinimumDate(today.endOf('day'), today.endOf('year')).toDate() : today.endOf('year').toDate()
  ];
};

export const getLastYearDateRange = (): [Date, Date] => {
  const lastYear = dayjs().subtract(1, 'year');

  return [lastYear.startOf('year').toDate(), lastYear.endOf('year').toDate()];
};

/**
 * To RETURN the earlier date of either date inputs
 *
 * @param dateA: First date in dayjs.Dayjs
 * @param dateB: Second date in dayjs.Dayjs
 *
 * This method can be used in place of dayjs.min(), as dayjs.min() is throwing null if no date or only one date is passed as parameters
 */
export const getMinimumDate = (dateA: dayjs.Dayjs, dateB: dayjs.Dayjs) => {
  const minimumDate = dayjs.min(dateA, dateB);

  if (minimumDate === null) {
    return dateA.isAfter(dateB) ? dateB : dateA;
  } else {
    return minimumDate;
  }
};

export const getUTCOffsetDateTimeAsDayjs = ({
  input,
  utcOffset,
  keepTime,
  baseFromOffset
}: IGetUTCOffsetDateTimeAsDayjsParams = {}) => {
  let dayjsObj = dayjs(input);
  if (utcOffset !== undefined) {
    dayjsObj = dayjsObj.utcOffset(utcOffset, keepTime);
  }
  return baseFromOffset ? dayjs(dayjsObj.format(DATETIME_SECONDS_FORMAT)) : dayjsObj;
};
